import React from 'react';

import { Table, Tbody, Td, Th, Thead, Tr, Button } from '@chakra-ui/react';
import { useTheme } from '@chakra-ui/system';
import { CircularProgress } from '@chakra-ui/progress';
import { Box, Center, Flex } from '@chakra-ui/layout';

import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { DataTableProps, ServerSort } from './DataTable.props';
import TablePagination from './TablePagination';
import { TABLE_FRAME_COLOR, TABLE_GRID_COLOR } from './tableBorders';

/**
 * Passed through Chakra's `sx` (NOT the raw emotion `css` prop) so the color
 * tokens resolve — under `css` they reached the browser as the literal string
 * "primary.400", leaving an invisible scrollbar thumb over a cropped table.
 * The webkit rules also opt the box out of macOS overlay scrollbars, so the
 * bar stays visible instead of appearing only mid-scroll.
 */
const scrollbarSx = {
  // Firefox ignores ::-webkit-scrollbar; these are its equivalent knobs.
  scrollbarWidth: 'thin' as const,
  scrollbarColor: 'var(--chakra-colors-primary-400) transparent',
  '&::-webkit-scrollbar': {
    width: '10px',
    height: '10px',
  },
  '&::-webkit-scrollbar-track': {
    bg: 'primary.900',
    borderRadius: '24px',
  },
  '&::-webkit-scrollbar-thumb': {
    bg: 'primary.400',
    borderRadius: '24px',
  },
};

/** TanStack's default column size; treated as "let the browser size it". */
const AUTO_SIZE = 150;

/** Cap applied to truncating cells that don't declare their own size. */
const TRUNCATE_MAX_W = 320;

const nextSort = (sort: ServerSort | undefined, field: string): ServerSort => ({
  field,
  // First click on a column sorts descending, matching the pre-existing
  // sortHandler behavior; clicks after that toggle.
  dir: sort?.field === field && sort.dir === -1 ? 1 : -1,
});

const DataTableView = <T extends object>(props: DataTableProps<T>) => {
  const theme = useTheme();

  // Same band colors as CustomTable, so migrated and unmigrated screens
  // stay visually identical during the incremental rollout.
  const pc = theme?.colors?.primary['800'];

  const {
    data,
    columns,
    isLoading,
    pagination,
    sort,
    onSortChange,
    onRowClick,
    getRowId = (row, index) => (row as any)?._id ?? String(index),
    mt = 2,
    minW,
    maxH = '52vh',
    stickyHeader = true,
    fitContent = false,
    overflowX = 'auto',
    renderEmpty = () => <></>,
  } = props;

  const table = useReactTable({
    data: data as T[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    getRowId,
    defaultColumn: { minSize: 60 },
  });

  const leafColumns = table.getAllLeafColumns();

  // Left offset of each pinned column, summed from the widths of the pinned
  // columns before it. Pinned columns are expected to lead the column list,
  // which is what makes a running total the right offset.
  const pinnedOffsets = new Map<string, number>();
  let pinnedRunningWidth = 0;
  leafColumns.forEach((column) => {
    if (column.columnDef.meta?.pinned) {
      pinnedOffsets.set(column.id, pinnedRunningWidth);
      pinnedRunningWidth += Number(column.columnDef.size) || 0;
    }
  });

  /** Sticky positioning for a pinned column, or nothing for a normal one. */
  const pinnedStyles = (columnId: string, zIndex: number) => {
    const left = pinnedOffsets.get(columnId);

    if (left === undefined) {
      return {};
    }

    return { position: 'sticky' as const, left, zIndex };
  };

  return (
    <Flex direction="column" pos="relative" mt={mt}>
      {/* One box owns BOTH scroll axes, so the horizontal scrollbar sits at
          the bottom of the visible window (at most maxH away) instead of
          under the full table where small screens never see it. The sticky
          header sticks against this same box. `minWidth: 0` keeps the
          overflow here rather than letting it escape upward and push the
          whole page sideways: as a flex item this box's automatic minimum
          is its content's width. */}
      <Box
        // A fit-content table gets a fit-content frame, so the border hugs
        // the columns instead of running on across empty space.
        w={fitContent ? 'fit-content' : '100%'}
        maxW="100%"
        minW={0}
        maxH={maxH}
        overflowX={overflowX}
        overflowY="auto"
        border="1px solid"
        borderColor={TABLE_FRAME_COLOR}
        borderRadius="8px"
        sx={scrollbarSx}>
        <Table
          variant="unstyled"
          // auto lets the columns keep their own widths when there are few of
          // them, rather than the table filling the box and spreading them to
          // the far edges (86bbmcv2b).
          w={fitContent ? 'auto' : '100%'}
          minW={minW}
          fontSize="13px"
          color="support.500"
          sx={{
            // `separate` rather than `collapse`: collapsed borders are painted
            // by the table, not the cell, so they scroll away from a sticky
            // header or pinned column and leave it without its lines.
            borderCollapse: 'separate',
            borderSpacing: 0,
            // Grid lines: each cell draws its own bottom and right edge. The
            // last column and the last row leave theirs to the frame, so no
            // edge is ever drawn twice.
            'th, td': {
              borderBottom: '1px solid',
              borderBottomColor: TABLE_GRID_COLOR,
              borderRight: '1px solid',
              borderRightColor: TABLE_GRID_COLOR,
              // Keep zebra/hover fills inside the lines.
              backgroundClip: 'padding-box',
            },
            'th:last-of-type, td:last-of-type': { borderRight: 'none' },
            'tbody tr:last-of-type td': { borderBottom: 'none' },
            // Zebra and hover fills are translucent, so a pinned cell needs
            // white composited underneath them rather than instead of them:
            // without it the columns scrolling past show through, and with a
            // plain opaque override the pinned cells lose the banding the
            // rest of the row has. Both rules therefore layer the fill over
            // white for pinned cells and stay translucent elsewhere.
            'tbody tr:nth-of-type(even) td': { bg: `${pc}30` },
            'tbody tr:nth-of-type(even) td[data-pinned]': {
              background: `linear-gradient(${pc}30, ${pc}30), #fff`,
            },
            'tbody tr:nth-of-type(odd) td[data-pinned]': { background: '#fff' },
            'tbody tr:hover td': { bg: 'primary.800', transition: '0.3s' },
            'tbody tr:hover td[data-pinned]': {
              background:
                'linear-gradient(var(--chakra-colors-primary-800), var(--chakra-colors-primary-800)), #fff',
            },
          }}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const { columnDef } = header.column;
                  const meta = columnDef.meta;
                  const sortField = meta?.sortField;
                  const isSorted = sort?.field === sortField;

                  return (
                    <Th
                      key={header.id}
                      // The band color composited over white, so the stuck
                      // header is OPAQUE — with the raw `${pc}80` alpha,
                      // rows scrolling beneath showed through and made the
                      // header look like it shifted.
                      background={`linear-gradient(${pc}80, ${pc}80), #fff`}
                      px={3}
                      py={4}
                      fontSize="13px"
                      fontWeight="bold"
                      color="support.500"
                      textTransform="none"
                      letterSpacing="normal"
                      userSelect="none"
                      // Header text is always centered, whatever the
                      // column's cell alignment; meta.align only aligns
                      // the cells below it.
                      textAlign="center"
                      {...(stickyHeader && {
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      })}
                      // A pinned header is stuck on both axes, so it has to
                      // outrank both the scrolling headers and the pinned
                      // body cells passing beneath it.
                      {...pinnedStyles(header.column.id, 3)}
                      {...(pinnedOffsets.has(header.column.id) &&
                        stickyHeader && { top: 0 })}
                      verticalAlign="middle"
                      style={{
                        minWidth: columnDef.minSize,
                        width:
                          columnDef.size !== AUTO_SIZE
                            ? columnDef.size
                            : undefined,
                      }}>
                      {/* Sortable and plain headers share this wrapper so
                            both sit on the same line box — a bare text node
                            and a Button otherwise center a couple of pixels
                            apart. */}
                      <Flex
                        align="center"
                        minH="24px"
                        lineHeight="normal"
                        justify="center">
                        {sortField && onSortChange ? (
                          <Button
                            variant="plain"
                            fontSize="13px"
                            lineHeight="normal"
                            color="support.500"
                            rightIcon={
                              isSorted ? (
                                sort!.dir === 1 ? (
                                  <FaSortUp />
                                ) : (
                                  <FaSortDown />
                                )
                              ) : (
                                <FaSort />
                              )
                            }
                            onClick={() =>
                              onSortChange(nextSort(sort, sortField))
                            }>
                            {flexRender(columnDef.header, header.getContext())}
                          </Button>
                        ) : (
                          flexRender(columnDef.header, header.getContext())
                        )}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {isLoading ? (
              <Tr>
                <Td colSpan={leafColumns.length}>
                  <Center py={20} px={5}>
                    <CircularProgress isIndeterminate color="primary.400" />
                  </Center>
                </Td>
              </Tr>
            ) : data.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  cursor={onRowClick ? 'pointer' : 'default'}
                  onClick={() => onRowClick?.(row.original)}>
                  {row.getVisibleCells().map((cell) => {
                    const { columnDef } = cell.column;
                    const meta = columnDef.meta;
                    const truncate = meta?.truncate !== false;
                    const value = cell.getValue();
                    const title =
                      truncate &&
                      (typeof value === 'string' || typeof value === 'number')
                        ? String(value)
                        : undefined;

                    const content = flexRender(
                      columnDef.cell,
                      cell.getContext(),
                    );

                    return (
                      <Td
                        key={cell.id}
                        px={3}
                        py={4}
                        title={title}
                        verticalAlign="middle"
                        lineHeight="normal"
                        {...(truncate && {
                          maxW:
                            columnDef.size !== AUTO_SIZE
                              ? `${columnDef.size}px`
                              : `${TRUNCATE_MAX_W}px`,
                        })}
                        {...pinnedStyles(cell.column.id, 1)}
                        // Flagged rather than styled inline: the zebra and
                        // hover rules above are descendant selectors and
                        // outrank a style prop, so the opaque backing has to
                        // be applied from the same stylesheet.
                        {...(pinnedOffsets.has(cell.column.id) && {
                          'data-pinned': true,
                        })}>
                        {/* Every cell renders through the same centered flex
                            box (matching the header wrapper), so text, tags
                            and numbers all sit on one visual centerline
                            instead of each element's own line box. */}
                        <Flex
                          align="center"
                          w="100%"
                          minH="20px"
                          justify={
                            meta?.align === 'center'
                              ? 'center'
                              : meta?.align === 'right'
                              ? 'flex-end'
                              : 'flex-start'
                          }>
                          {truncate ? (
                            <Box
                              minW={0}
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap">
                              {content}
                            </Box>
                          ) : (
                            // Block wrapper so multi-line cell content (text
                            // with <br/>s, stacked pills) keeps its inline
                            // flow — as a direct flex child it would be
                            // flattened onto one row.
                            <Box minW={0}>{content}</Box>
                          )}
                        </Flex>
                      </Td>
                    );
                  })}
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={leafColumns.length}>{renderEmpty()}</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
      {pagination && (
        <TablePagination pagination={pagination} hasData={data.length > 0} />
      )}
    </Flex>
  );
};

export default DataTableView;
